import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Loader2, Check, Infinity as InfinityIcon, Receipt, Heart } from "lucide-react";
import { toast } from "sonner";
import { SupporterBadge } from "@/components/SupporterBadge";

const PACKS = [
  { pack: "credits_100", credits: 100, price: "$49.99", tag: "" },
  { pack: "credits_500", credits: 500, price: "$199.99", tag: "Best value" },
  { pack: "credits_1000", credits: 1000, price: "$499.99", tag: "Power user" },
];

export default function Credits() {
  const { user } = useAuth();
  const [loading, setLoading] = useState("");
  const [history, setHistory] = useState([]);
  const balance = user?.ai_credits ?? 0;

  useEffect(() => {
    api.get("/payments/credits/history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const buy = async (pack) => {
    setLoading(pack);
    try {
      const { data } = await api.post("/payments/credits/checkout", {
        origin_url: window.location.origin, pack,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading("");
    }
  };

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return d; } };
  const fmtAmt = (a, c) => `${(a / 100).toLocaleString(undefined, { style: "currency", currency: (c || "usd").toUpperCase() })}`;

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-5xl">AI Credits</h1>
          <SupporterBadge total={user?.total_credits_purchased || 0} supporter={user?.supporter} className="ml-1" />
        </div>
        <p className="text-muted-foreground max-w-2xl mb-8">
          Your membership already includes <strong>unlimited</strong> use of the AI Legal Assistant. Credits are a way to
          <strong> support the Academy</strong> and fuel heavy drafting sessions — and every top-up earns you a supporter
          badge on your profile. Each AI prompt draws one credit when you hold a balance.
        </p>

        <div className="flex flex-wrap items-center gap-6 border border-border bg-card p-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest font-mono text-primary mb-1">Your balance</div>
            <div className="font-serif text-4xl" data-testid="credits-balance">{balance}</div>
            <div className="text-xs text-muted-foreground font-mono mt-1">credits</div>
          </div>
          <div className="flex flex-col items-end gap-2 text-sm text-muted-foreground ml-auto">
            <div className="flex items-center gap-2"><InfinityIcon className="w-5 h-5 text-primary" /> Unlimited assistant access included with membership</div>
            {(user?.total_credits_purchased || 0) > 0 && (
              <div className="flex items-center gap-2 text-primary" data-testid="supporter-thanks">
                <Heart className="w-4 h-4 fill-current" /> Thank you — you've supported the Academy with {user.total_credits_purchased.toLocaleString()} credits
              </div>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-px bg-border border border-border">
          {PACKS.map((p) => (
            <div key={p.pack} className="bg-card p-8 flex flex-col" data-testid={`credit-pack-${p.credits}`}>
              {p.tag ? (
                <span className="self-start text-[9px] uppercase tracking-widest font-mono bg-primary text-primary-foreground px-2 py-0.5 mb-4">{p.tag}</span>
              ) : (
                <span className="mb-4 h-[18px]" />
              )}
              <div className="font-serif text-4xl mb-1">{p.credits}</div>
              <div className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-6">credits</div>
              <div className="font-serif text-2xl text-primary mb-6">{p.price}</div>
              <ul className="text-sm text-muted-foreground space-y-2 mb-8">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" /> {p.credits} AI prompts</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" /> Never expires</li>
              </ul>
              <button onClick={() => buy(p.pack)} disabled={!!loading} data-testid={`buy-credits-${p.credits}`}
                className="mt-auto w-full bg-primary text-primary-foreground py-3 rounded-sm text-sm flex items-center justify-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
                {loading === p.pack ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Buy {p.credits}
              </button>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground font-mono text-center mt-8">
          Secure checkout by Stripe · Test card 4242 4242 4242 4242
        </p>

        <div className="mt-14" data-testid="credits-history">
          <div className="flex items-center gap-2.5 mb-5 pb-2 border-b border-border">
            <Receipt className="w-5 h-5 text-primary" />
            <h2 className="font-serif text-2xl">Purchase history</h2>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="credits-history-empty">
              No credit purchases yet. Your top-ups will appear here.
            </p>
          ) : (
            <div className="border border-border divide-y divide-border">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5" data-testid={`credits-history-row-${i}`}>
                  <Sparkles className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-serif text-lg">+{h.credits}</span>
                  <span className="text-xs uppercase tracking-widest font-mono text-muted-foreground">credits</span>
                  <span className="ml-auto text-sm text-foreground/80">{fmtAmt(h.amount, h.currency)}</span>
                  <span className="text-xs font-mono text-muted-foreground w-28 text-right">{fmtDate(h.date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
